import { useEffect, useState } from "react";
import { adminApi, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, DollarSign, CheckCircle2, RotateCcw, X, ArrowLeft, Crown } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { QrCard } from "@/components/admin/MenuQrCard";

export default function CommissionsManager() {
  const [rows, setRows] = useState(null);
  const [qrStaff, setQrStaff] = useState(null);
  const [detail, setDetail] = useState(null);
  const [refunding, setRefunding] = useState(null);

  async function load() {
    try {
      const { data } = await adminApi.get("/admin/commissions");
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }
  useEffect(() => { load(); }, []);

  async function openDetail(staff_id) {
    try {
      const { data } = await adminApi.get(`/admin/commissions/${staff_id}/detail`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  async function markPaid(staff_id) {
    if (!window.confirm("Mark all 'earned' commissions as paid?")) return;
    try {
      const { data } = await adminApi.post("/admin/commissions/mark-paid", { staff_id });
      toast.success(`Marked ${data.reservations_marked + data.tickets_marked} commissions paid`);
      load();
      if (detail) openDetail(staff_id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  async function refundTicket(ticket_id, amount = null) {
    if (!window.confirm(amount ? `Partial refund of $${amount}?` : "Full refund this ticket?")) return;
    setRefunding(ticket_id);
    try {
      await adminApi.post(`/admin/tickets/${ticket_id}/refund`, { amount });
      toast.success("Refund processed");
      load();
      if (detail?.staff?.id) openDetail(detail.staff.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setRefunding(null);
    }
  }

  if (rows === null) {
    return (
      <div className="flex items-center gap-2 text-sm text-[var(--trio-muted)]">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading commissions…
      </div>
    );
  }

  if (detail) {
    return (
      <CommissionDetail
        data={detail}
        onBack={() => setDetail(null)}
        onMarkPaid={() => markPaid(detail.staff.id)}
        onRefund={refundTicket}
        refunding={refunding}
      />
    );
  }

  const grandPending = rows.reduce((s, r) => s + (r.reservations.pending + r.tickets.pending), 0);
  const grandEarned = rows.reduce((s, r) => s + (r.reservations.earned + r.tickets.earned), 0);
  const grandPaid = rows.reduce((s, r) => s + (r.reservations.paid + r.tickets.paid), 0);

  return (
    <div className="space-y-6" data-testid="admin-commissions">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display uppercase text-3xl tracking-tight">Commissions</h2>
          <p className="text-sm text-[var(--trio-muted)] mt-1">
            Earnings from staff referral links. Pending → Earned when a reservation is confirmed or a ticket is paid.
          </p>
        </div>
        <button
          onClick={async () => {
            if (!window.confirm("Snapshot the current month's leaderboard right now and email the winner to the whole crew?")) return;
            try {
              const { data } = await adminApi.post("/admin/leaderboard/close-month-now");
              if (data?.winner) toast.success(`Closed month — winner is ${data.winner.name} at $${data.winner.total.toFixed(2)}`);
              else toast.info("Month closed — no earned commissions yet.");
            } catch (e) {
              toast.error(formatApiError(e.response?.data?.detail));
            }
          }}
          data-testid="admin-close-month-now"
          className="inline-flex items-center gap-2 border border-[var(--trio-accent)] text-[var(--trio-text)] hover:bg-[var(--trio-accent)] px-4 py-2 text-xs tracking-widest uppercase font-bold"
          title="Force-snapshot the current month & email the winner"
        >
          <Crown className="w-4 h-4" /> Close Month Now
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <SummaryTile label="Pending" value={grandPending} tone="muted" />
        <SummaryTile label="Earned (unpaid)" value={grandEarned} tone="accent" />
        <SummaryTile label="Paid out" value={grandPaid} tone="primary" />
      </div>

      {rows.length === 0 ? (
        <div className="border border-dashed border-[var(--trio-border)] p-10 text-center text-sm text-[var(--trio-muted)]">
          No staff yet — add employees via the Schedule tab so they can start earning.
        </div>
      ) : (
        <div className="border border-[var(--trio-border)] bg-[var(--trio-surface)] overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] tracking-widest uppercase text-[var(--trio-muted)] border-b border-[var(--trio-border)]">
                <th className="px-4 py-3">Staff</th>
                <th className="px-4 py-3">Ref Code</th>
                <th className="px-4 py-3 text-right">Reservations</th>
                <th className="px-4 py-3 text-right">Tickets</th>
                <th className="px-4 py-3 text-right">Earned</th>
                <th className="px-4 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--trio-border)]">
              {rows.map((r) => {
                const earned = r.reservations.earned + r.tickets.earned;
                return (
                  <tr key={r.staff.id} data-testid={`comm-row-${r.staff.referral_code}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{r.staff.name}</div>
                      <div className="text-xs text-[var(--trio-muted)]">{r.staff.email}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{r.staff.referral_code || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="text-xs text-[var(--trio-muted)]">{r.reservations.count} total</div>
                      <div>${(r.reservations.earned + r.reservations.paid).toFixed(2)}</div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="text-xs text-[var(--trio-muted)]">{r.tickets.count} total</div>
                      <div>${(r.tickets.earned + r.tickets.paid).toFixed(2)}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-display text-xl text-[var(--trio-primary)]">
                      ${earned.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-2">
                        <button
                          onClick={() => openDetail(r.staff.id)}
                          data-testid={`comm-view-${r.staff.referral_code}`}
                          className="text-xs tracking-widest uppercase border border-[var(--trio-border)] hover:border-[var(--trio-primary)] px-3 py-1.5"
                        >
                          View
                        </button>
                        {r.staff.referral_code && (
                          <button
                            onClick={() => setQrStaff(r.staff)}
                            data-testid={`comm-qr-${r.staff.referral_code}`}
                            className="text-xs tracking-widest uppercase border border-[var(--trio-border)] hover:border-[var(--trio-primary)] px-3 py-1.5"
                          >
                            QR / Link
                          </button>
                        )}
                        {earned > 0 && (
                          <button
                            onClick={() => markPaid(r.staff.id)}
                            data-testid={`comm-pay-${r.staff.referral_code}`}
                            className="text-xs tracking-widest uppercase bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-3 py-1.5"
                          >
                            Mark Paid
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={!!qrStaff} onOpenChange={(o) => !o && setQrStaff(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-2xl rounded-none p-0 border-0">
          <DialogTitle className="sr-only">Staff QR code</DialogTitle>
          {qrStaff && (
            <QrCard
              url={`${window.location.origin}/r/${qrStaff.referral_code}`}
              label={`${qrStaff.name}'s Booking QR`}
              title={`Book With ${qrStaff.name.split(" ")[0]}`}
              description="Guests who scan this or open the link can RSVP or buy tickets, and the sale is credited to this team member."
              printHeading={`Book With ${qrStaff.name.split(" ")[0]} at Trio`}
              filename={`trio-${qrStaff.referral_code}-qr.png`}
              testid="staff-qr-card"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryTile({ label, value, tone }) {
  const color = tone === "accent" ? "var(--trio-accent)" : tone === "primary" ? "var(--trio-primary)" : "var(--trio-muted)";
  return (
    <div className="border border-[var(--trio-border)] bg-[var(--trio-surface)] p-4">
      <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-muted)] font-bold">{label}</div>
      <div className="font-display text-4xl mt-1" style={{ color }}>
        ${Number(value || 0).toFixed(2)}
      </div>
    </div>
  );
}

function CommissionDetail({ data, onBack, onMarkPaid, onRefund, refunding }) {
  const combined = [
    ...(data.reservations || []).map((r) => ({ kind: "reservation", ...r })),
    ...(data.tickets || []).map((t) => ({ kind: "ticket", ...t })),
  ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));

  return (
    <div className="space-y-5" data-testid="admin-commission-detail">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-[var(--trio-muted)] hover:text-[var(--trio-text)]">
        <ArrowLeft className="w-4 h-4" /> Back to all staff
      </button>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display uppercase text-3xl tracking-tight">{data.staff.name}</h2>
          <div className="text-sm text-[var(--trio-muted)]">
            {data.staff.email} · code <span className="font-mono">{data.staff.referral_code}</span>
          </div>
        </div>
        <button
          onClick={onMarkPaid}
          className="inline-flex items-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-4 py-2 text-xs tracking-widest uppercase font-bold"
        >
          <CheckCircle2 className="w-4 h-4" /> Mark Earned → Paid
        </button>
      </div>

      {combined.length === 0 ? (
        <div className="border border-dashed border-[var(--trio-border)] p-10 text-center text-sm text-[var(--trio-muted)]">
          No commissions recorded yet.
        </div>
      ) : (
        <div className="border border-[var(--trio-border)] bg-[var(--trio-surface)] overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] tracking-widest uppercase text-[var(--trio-muted)] border-b border-[var(--trio-border)]">
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Detail</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--trio-border)]">
              {combined.map((row, i) => {
                const isTicket = row.kind === "ticket";
                return (
                  <tr key={`${row.kind}-${row.id || i}`}>
                    <td className="px-4 py-3 text-xs text-[var(--trio-muted)] whitespace-nowrap">
                      {(row.created_at || "").slice(0, 10)}
                    </td>
                    <td className="px-4 py-3">{isTicket ? "Ticket" : "Reservation"}</td>
                    <td className="px-4 py-3">
                      {isTicket ? (
                        <>
                          <div className="font-medium">{row.event_title}</div>
                          <div className="text-xs text-[var(--trio-muted)]">
                            {row.buyer_name} · {row.quantity} × ${(row.unit_price || 0).toFixed(2)}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="font-medium">{row.name}</div>
                          <div className="text-xs text-[var(--trio-muted)]">
                            {row.party_size} guests · {row.date} · {row.status}
                          </div>
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-display text-lg">
                      ${Number(row.commission_amount || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={row.commission_status || "pending"} />
                      {isTicket && (
                        <div className="text-[10px] text-[var(--trio-muted)] mt-1 uppercase tracking-widest">
                          Payment: {row.payment_status}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {isTicket && row.payment_status === "paid" && (
                        <button
                          disabled={refunding === row.id}
                          onClick={() => onRefund(row.id, null)}
                          className="text-xs tracking-widest uppercase border border-red-500 text-red-600 hover:bg-red-500 hover:text-white px-3 py-1.5 disabled:opacity-60"
                          data-testid={`refund-ticket-${row.id}`}
                        >
                          {refunding === row.id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Refund"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const color =
    status === "earned" ? "bg-[var(--trio-accent)] text-black"
    : status === "paid" ? "bg-green-600 text-white"
    : status === "canceled" ? "bg-red-100 text-red-700"
    : "bg-[var(--trio-border)] text-[var(--trio-muted)]";
  return (
    <span className={`inline-block text-[9px] tracking-widest uppercase px-1.5 py-0.5 ${color}`}>
      {status}
    </span>
  );
}
